"use client"

import { useState, useEffect, useContext, useMemo } from "react"
import {
  Box,
  Grid,
  TextField,
  Button,
  Autocomplete,
  Card,
  CardContent,
  Typography,
  Paper,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  alpha,
} from "@mui/material"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts"
import { ErpContext } from "../../context/ErpContext.js"

// Icons
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import ReceiptIcon from "@mui/icons-material/Receipt"
import InfoIcon from "@mui/icons-material/Info"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import BarChartIcon from "@mui/icons-material/BarChart"
import PieChartIcon from "@mui/icons-material/PieChart"
import ShowChartIcon from "@mui/icons-material/ShowChart"

// Tema personalizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
        },
      },
    },
  },
})

const CajaComponentAnaliticos = () => {
  const {
    getCajaByMedioDePago,
    getMedioDePago,
    tipoMedioDePago,
    medioDePagoIdTipoEspecifico,
    getTipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
  } = useContext(ErpContext)

  // Fechas del mes actual
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10)

  const [startDate, setStartDate] = useState(firstDay)
  const [endDate, setEndDate] = useState(lastDay)
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState(null)
  const [movimientos, setMovimientos] = useState([])
  const [caja, setCaja] = useState([])
  const [loading, setLoading] = useState(false)

  // Función para formatear fecha correctamente (evita problemas de zona horaria)
  const formatearFecha = (fechaString) => {
    if (!fechaString) return ""

    // Si la fecha viene en formato YYYY-MM-DD, crear la fecha directamente
    if (typeof fechaString === "string" && fechaString.includes("-")) {
      const [year, month, day] = fechaString.split("T")[0].split("-")
      const fecha = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
      return fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })
    }

    // Para otros formatos, usar el método estándar
    const fecha = new Date(fechaString)
    return fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })
  }

  // Función para formatear fecha completa
  const formatearFechaCompleta = (fechaString) => {
    if (!fechaString) return ""

    if (typeof fechaString === "string" && fechaString.includes("-")) {
      const [year, month, day] = fechaString.split("T")[0].split("-")
      const fecha = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
      return fecha.toLocaleDateString("es-AR")
    }

    const fecha = new Date(fechaString)
    return fecha.toLocaleDateString("es-AR")
  }

  // Función para extraer solo la fecha (YYYY-MM-DD) sin problemas de zona horaria
  const extraerFecha = (fechaString) => {
    if (!fechaString) return ""

    // Si ya es una fecha en formato YYYY-MM-DD, devolverla tal como está
    if (typeof fechaString === "string" && fechaString.includes("-")) {
      return fechaString.split("T")[0] // Remover la parte de tiempo si existe
    }

    // Si es un objeto Date, convertirlo a formato local
    const fecha = new Date(fechaString)
    const year = fecha.getFullYear()
    const month = String(fecha.getMonth() + 1).padStart(2, "0")
    const day = String(fecha.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  // === Carga inicial de datos ===
  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    await getMedioDePago()
    await getTipoMedioDePago()
    console.log('va a handleFiltrar')
    await handleFiltrar()
  }

  // Al seleccionar categoría, cargo medios filtrados
  useEffect(() => {
    if (selectedTipoMedioDePago) {
      getMedioDePagoIdTipoEspecifico(selectedTipoMedioDePago)
    }
  }, [selectedTipoMedioDePago])

  const handleFiltrar = async () => {
    console.log(selectedMedioDePago, selectedTipoMedioDePago)
    try {
      setLoading(true)
      const response = await getCajaByMedioDePago({
        startDate,
        endDate,
        medioDePagoId: selectedMedioDePago? selectedMedioDePago.id : null,
        tipoMedioDePagoId: selectedTipoMedioDePago ? selectedTipoMedioDePago.id : null,
      })

      console.log("Datos de caja filtrados:", response.data)

      const { movimientos: movs, caja: cajaData } = response.data
      setMovimientos(movs)
      setCaja(cajaData)
    } catch (error) {
      console.error("Error al filtrar caja:", error)
    } finally {
      setLoading(false)
    }
  }

  // Cálculos y análisis de datos
  const analytics = useMemo(() => {
    if (!movimientos.length) return null

    const totalIngresos = movimientos.filter((m) => m.monto > 0).reduce((sum, m) => sum + m.monto, 0)
    const totalGastado = movimientos.filter((m) => m.monto < 0).reduce((sum, m) => sum + Math.abs(m.monto), 0)
    const netoTotal = totalIngresos - totalGastado

    // Análisis por tipo de transacción
    const porTipoTransaccion = movimientos.reduce((acc, mov) => {
      const tipo = mov.tipoTransaccion
      if (!acc[tipo]) {
        acc[tipo] = { ingresos: 0, gastos: 0, cantidad: 0 }
      }
      if (mov.monto > 0) {
        acc[tipo].ingresos += mov.monto
      } else {
        acc[tipo].gastos += Math.abs(mov.monto)
      }
      acc[tipo].cantidad += 1
      return acc
    }, {})

    // Análisis por fecha - CORREGIDO para evitar problemas de zona horaria
    const porFecha = movimientos.reduce((acc, mov) => {
      const fechaKey = extraerFecha(mov.fecha) // Usar la función corregida
      if (!acc[fechaKey]) {
        acc[fechaKey] = { ingresos: 0, gastos: 0, transacciones: 0 }
      }
      if (mov.monto > 0) {
        acc[fechaKey].ingresos += mov.monto
      } else {
        acc[fechaKey].gastos += Math.abs(mov.monto)
      }
      acc[fechaKey].transacciones += 1
      return acc
    }, {})

    // Promedio diario
    const diasConMovimientos = Object.keys(porFecha).length
    const promedioIngresosDiario = diasConMovimientos > 0 ? totalIngresos / diasConMovimientos : 0
    const promedioGastosDiario = diasConMovimientos > 0 ? totalGastado / diasConMovimientos : 0

    // Día con mayor movimiento
    const diaConMayorMovimiento = Object.entries(porFecha).reduce(
      (max, [fecha, datos]) => {
        const total = datos.ingresos + datos.gastos
        return total > max.total ? { fecha, total, ...datos } : max
      },
      { fecha: "", total: 0, ingresos: 0, gastos: 0, transacciones: 0 },
    )

    return {
      totalIngresos,
      totalGastado,
      netoTotal,
      porTipoTransaccion,
      porFecha,
      promedioIngresosDiario,
      promedioGastosDiario,
      diaConMayorMovimiento,
      diasConMovimientos,
      totalTransacciones: movimientos.length,
    }
  }, [movimientos])

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(value)
  }

  // Datos para gráficos
  const pieData = analytics
    ? Object.entries(analytics.porTipoTransaccion).map(([tipo, datos]) => ({
        name: tipo,
        value: datos.ingresos + datos.gastos,
        ingresos: datos.ingresos,
        gastos: datos.gastos,
        cantidad: datos.cantidad,
      }))
    : []

  // Datos para gráfico de barras agrupados por mes (solo neto)
  const barDataPorMes = analytics
    ? (() => {
        // Agrupar por mes
        const porMes = Object.entries(analytics.porFecha).reduce((acc, [fechaKey, datos]) => {
          const fecha = new Date(fechaKey)
          const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}`
          
          if (!acc[mesKey]) {
            acc[mesKey] = { ingresos: 0, gastos: 0 }
          }
          
          acc[mesKey].ingresos += datos.ingresos
          acc[mesKey].gastos += datos.gastos
          
          return acc
        }, {})
        
        // Convertir a array y calcular neto
        return Object.entries(porMes)
          .map(([mesKey, datos]) => {
            const [year, month] = mesKey.split("-")
            const fecha = new Date(Number.parseInt(year), Number.parseInt(month) - 1, 1)
            const nombreMes = fecha.toLocaleDateString("es-AR", { month: "long", year: "numeric" })
            
            return {
              mes: nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1),
              mesKey: mesKey, // Para ordenamiento
              neto: datos.ingresos - datos.gastos,
            }
          })
          .sort((a, b) => a.mesKey.localeCompare(b.mesKey)) // Ordenar por mes
      })()
    : []

  // Colores para gráficos
  const COLORS = [
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.secondary.main,
  ]

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            Análisis de Caja
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visualiza y analiza el flujo de efectivo por medio de pago en tiempo real
          </Typography>
        </Paper>

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="Desde"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="Hasta"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={tipoMedioDePago}
                getOptionLabel={(opt) => opt.descripcion}
                value={selectedTipoMedioDePago}
                onChange={(_, v) => setSelectedTipoMedioDePago(v)}
                renderInput={(params) => <TextField {...params} label="Categoría Medio" size="small" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={medioDePagoIdTipoEspecifico}
                getOptionLabel={(opt) => opt.descripcion}
                value={selectedMedioDePago}
                onChange={(_, v) => setSelectedMedioDePago(v)}
                renderInput={(params) => <TextField {...params} label="Medio de Pago" size="small" />}
                disabled={!selectedTipoMedioDePago}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={handleFiltrar}
                fullWidth
                size="large"
                disabled={loading}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": { backgroundColor: theme.palette.secondary.main },
                }}
              >
                {loading ? "Cargando..." : "Buscar"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {analytics && (
          <>
            {/* Tarjetas de resumen principales */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <TrendingUpIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Total de dinero que ingresó a la caja">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Total Ingresado
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(analytics.totalIngresos)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      Promedio diario: {formatCurrency(analytics.promedioIngresosDiario)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${theme.palette.error.main}, ${alpha(theme.palette.error.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <TrendingDownIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Total de dinero que salió de la caja">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Total Gastado
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(analytics.totalGastado)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      Promedio diario: {formatCurrency(analytics.promedioGastosDiario)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${analytics.netoTotal >= 0 ? theme.palette.info.main : theme.palette.warning.main}, ${alpha(analytics.netoTotal >= 0 ? theme.palette.info.main : theme.palette.warning.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Diferencia entre ingresos y gastos (Ingresos - Gastos)">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Balance Neto
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(analytics.netoTotal)}
                    </Typography>
                    <Chip
                      label={analytics.netoTotal >= 0 ? "Positivo" : "Negativo"}
                      size="small"
                      sx={{
                        mt: 1,
                        backgroundColor: "rgba(255,255,255,0.2)",
                        color: "white",
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${alpha(theme.palette.secondary.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <ReceiptIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Información sobre la actividad de transacciones">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Actividad
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {analytics.totalTransacciones}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      En {analytics.diasConMovimientos} días
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Gráficos */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Gráfico de distribución por tipo de transacción */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: 400 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PieChartIcon color="primary" />
                      Distribución por Tipo de Transacción
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Muestra cómo se distribuye el dinero según el tipo de operación
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          labelLine={false}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name) => [formatCurrency(value), name]}
                          contentStyle={{
                            borderRadius: 8,
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Gráfico de evolución temporal */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: 400 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ShowChartIcon color="primary" />
                      Evolución Temporal
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Balance neto agregado por mes (Ingresos - Gastos)
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={barDataPorMes}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mes" />
                        <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                        <RechartsTooltip
                          formatter={(value) => [formatCurrency(value), "Neto"]}
                          contentStyle={{
                            borderRadius: 8,
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Bar
                          dataKey="neto"
                          radius={[4, 4, 0, 0]}
                        >
                          {barDataPorMes.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.neto >= 0 ? theme.palette.success.main : theme.palette.error.main}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {!analytics && !loading && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Selecciona los filtros y presiona "Filtrar" para ver el análisis de caja
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Podrás visualizar ingresos, gastos, tendencias y análisis detallados de tu flujo de efectivo
            </Typography>
          </Paper>
        )}
      </Box>
    </ThemeProvider>
  )
}

export default CajaComponentAnaliticos
